// Revenue (what ECO watches) vs Margin (what MCO watches) on ONE shared $/acre scale, so the
// gap between the columns is literally the input-cost bill. Hand-rolled SVG rather than the
// dialog's usual Vega-Lite: the layout is two fixed columns with labelled threshold rules and a
// dashed cost box between them, which is the reference app's geometry
// (datanac-core-dbx/products/mco_eco_app_reference/js/comparator.js) ported as-is.

import { Box } from "@mui/material";

const BASE = 278;
const TOP = 26;
const W = 130;
const X_REVENUE = 175;
const X_MARGIN = 445;

const COLOR_BAR = "#cfe6d6";
const COLOR_TRIGGER = "#8a4a3a";
const COLOR_MAX = "#8a929b";
const COLOR_TEXT = "#1f2328";
const COLOR_MUTED = "#57606a";

const fm = (v) => "$" + Math.round(v).toLocaleString("en-US");
const f2 = (v) =>
  "$" +
  (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Trigger + max-payment rules for one column, nudged apart when they nearly coincide. */
function ThresholdLines({ cx, trigger, maxAt, triggerLabel, maxLabel, suffix, dashed }) {
  const triggerY = trigger.y;
  const maxY = maxAt.y;
  const triggerTextY = triggerY + 4;
  const maxTextY = Math.max(maxY + 4, triggerTextY + 13);
  return (
    <>
      <line
        x1={cx - W / 2 - 6}
        y1={triggerY}
        x2={cx + W / 2 + 6}
        y2={triggerY}
        stroke={COLOR_TRIGGER}
        strokeWidth={dashed ? 1.4 : 1.8}
        strokeDasharray={dashed ? "5 4" : "none"}
      />
      <text x={cx + W / 2 + 12} y={triggerTextY} fontSize={11} fill={COLOR_TRIGGER}>
        {triggerLabel}
        {suffix}
      </text>
      <line
        x1={cx - W / 2 - 6}
        y1={maxY}
        x2={cx + W / 2 + 6}
        y2={maxY}
        stroke={COLOR_MAX}
        strokeWidth={1.2}
        strokeDasharray={dashed ? "2 4" : "4 3"}
      />
      <text x={cx + W / 2 + 12} y={maxTextY} fontSize={11} fill={COLOR_MAX}>
        {maxLabel}
        {suffix}
      </text>
    </>
  );
}

/** @param {object} props.scenario — buildEcoMcoScenario result (ready) */
export default function McoEcoScenarioChart({ scenario }) {
  const { revenue, margin, stress, estimates, ecoRecomputed, mcoRecomputed } = scenario;

  // Floor the scale at $300 so a collapsed margin does not zoom the chart into meaninglessness.
  const yLo = Math.floor(Math.min(margin.maxAt, margin.current, 300) / 50) * 50 - 50;
  const yHi = Math.ceil(Math.max(revenue.current, revenue.trigger) / 50) * 50 + 50;
  const Y = (v) => BASE - ((v - yLo) / (yHi - yLo)) * (BASE - TOP);
  const at = (v) => ({ y: Y(clamp(v, yLo, yHi)) });

  const gridStep = Math.ceil((yHi - yLo) / 6 / 50) * 50;
  const gridlines = [];
  for (let g = yLo; g <= yHi; g += gridStep) gridlines.push(g);

  const revenueTop = Y(clamp(revenue.current, yLo, yHi));
  const marginTop = Y(clamp(margin.current, yLo, yHi));
  const marginOnScale = margin.current > yLo;
  const costBoxHeight = marginTop - revenueTop;
  const suffixEco = estimates ? " (est.)" : ecoRecomputed ? " (recomputed)" : "";
  const suffixMco = estimates ? " (est.)" : mcoRecomputed ? " (recomputed)" : "";

  return (
    <Box
      component="svg"
      viewBox="0 0 620 322"
      role="img"
      aria-label="Revenue and margin against their trigger lines"
      sx={{ width: "100%", height: 322, display: "block" }}
    >
      {gridlines.map((g) => (
        <g key={g}>
          <line x1={56} y1={Y(g)} x2={600} y2={Y(g)} stroke="rgba(0,0,0,.09)" strokeWidth={1} />
          <text x={48} y={Y(g) + 4} textAnchor="end" fontSize={11} fill={COLOR_MAX}>
            {"$" + g}
          </text>
        </g>
      ))}

      <text x={X_REVENUE} y={16} textAnchor="middle" fontSize={14} fontWeight={600} fill={COLOR_TEXT}>
        Revenue
      </text>
      <text x={X_MARGIN} y={16} textAnchor="middle" fontSize={14} fontWeight={600} fill={COLOR_TEXT}>
        Margin
      </text>

      <rect x={X_REVENUE - W / 2} y={revenueTop} width={W} height={BASE - revenueTop} fill={COLOR_BAR} />
      {marginOnScale && (
        <rect x={X_MARGIN - W / 2} y={marginTop} width={W} height={BASE - marginTop} fill={COLOR_BAR} />
      )}

      {/* The dashed box IS the input-cost bill — the one thing that separates the two columns. */}
      {costBoxHeight > 2 && (
        <>
          <rect
            x={X_MARGIN - W / 2}
            y={revenueTop}
            width={W}
            height={costBoxHeight}
            fill="none"
            stroke={COLOR_MAX}
            strokeWidth={1}
            strokeDasharray="4 3"
          />
          {costBoxHeight > 34 && (
            <text
              x={X_MARGIN}
              // Centred, but never within 24px of the margin value sitting above the bar.
              y={Math.min((revenueTop + marginTop) / 2 + 4, marginTop - 24)}
              textAnchor="middle"
              fontSize={12}
              fill={COLOR_MUTED}
            >
              {"costs " + fm(stress.harvestCosts)}
            </text>
          )}
        </>
      )}

      <ThresholdLines
        cx={X_REVENUE}
        trigger={at(revenue.trigger)}
        maxAt={at(revenue.maxAt)}
        triggerLabel={"pays below " + f2(revenue.trigger)}
        maxLabel={"maxed at " + f2(revenue.maxAt)}
        suffix={suffixEco}
        dashed={estimates}
      />
      <ThresholdLines
        cx={X_MARGIN}
        trigger={at(margin.trigger)}
        maxAt={at(margin.maxAt)}
        triggerLabel={"pays below " + f2(margin.trigger)}
        maxLabel={"maxed at " + f2(margin.maxAt)}
        suffix={suffixMco}
        dashed={estimates}
      />

      <text x={X_REVENUE} y={revenueTop - 8} textAnchor="middle" fontSize={15} fontWeight={600} fill={COLOR_TEXT}>
        {fm(revenue.current)}
      </text>
      <text
        x={X_MARGIN}
        y={marginOnScale ? marginTop - 8 : BASE - 8}
        textAnchor="middle"
        fontSize={15}
        fontWeight={600}
        fill={marginOnScale ? COLOR_TEXT : COLOR_TRIGGER}
      >
        {fm(margin.current) + (marginOnScale ? "" : " — below the scale")}
      </text>

      <text x={X_REVENUE} y={300} textAnchor="middle" fontSize={12} fill={COLOR_MUTED}>
        what ECO watches
      </text>
      <text x={X_MARGIN} y={300} textAnchor="middle" fontSize={12} fill={COLOR_MUTED}>
        what MCO watches
      </text>
    </Box>
  );
}
